import React from 'react';
import PropTypes from 'prop-types';
import LoadingState from 'components/kea/LoadingState';
import './FeaturePageShell.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

/**
 * FeaturePageShell — standardised chrome for normal feature pages.
 *
 * Render contract (04-layout-navigation-and-containers-v1.md — Feature Page Surface):
 * - Page header: title + optional short description.
 * - Primary action area (save / apply / discard / retry).
 * - Optional secondary help link area.
 * - Main content region with consistent vertical rhythm.
 * - Shared loading, error, and degraded states.
 * - Container width presets: 'default' | 'wide' | 'expanded' | 'fluid'.
 *
 * Usage:
 *   <FeaturePageShell
 *     title="Synonyms"
 *     description="Map equivalent search terms to the same results."
 *     actions={<Button onClick={handleSave}>Save</Button>}
 *     width="default"
 *     isLoading={isFetching}
 *     error={loadError}
 *   >
 *     <SynonymsTable ... />
 *   </FeaturePageShell>
 */

const WIDTH_CLASS = {
  default: 'fps-width-default',
  wide: 'fps-width-wide',
  expanded: 'fps-width-expanded',
  fluid: 'fps-width-fluid',
};

// ---------------------------------------------------------------------------
// Loading state
// ---------------------------------------------------------------------------
const FeaturePageLoading = () => <LoadingState variant="page" testId="fps-loading" />;

// ---------------------------------------------------------------------------
// Error / degraded state
// ---------------------------------------------------------------------------
const FeaturePageError = ({ error, onRetry }) => {
  const localizeMessage = useLocalizedMessage();
  return (
    <div className="fps-error" data-testid="fps-error" role="alert">
      <p className="fps-error-message">
        {typeof error === 'string' ? error : localizeMessage('ui.shell.featurePageShell.loadError')}
      </p>
      {onRetry && (
        <button type="button" className="fps-error-retry" onClick={onRetry}>
          <IntlMessages id="ui.shell.featurePageShell.retry" />
        </button>
      )}
    </div>
  );
};

FeaturePageError.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onRetry: PropTypes.func,
};

FeaturePageError.defaultProps = {
  error: null,
  onRetry: null,
};

export const FeaturePageHeader = ({ title, description, actions, secondaryActions, eyebrow }) => (
  <div className="fps-header">
    <div className="fps-header-text">
      {eyebrow && <div className="fps-eyebrow">{eyebrow}</div>}
      {title && <h1 className="fps-title" data-testid="fps-title">{title}</h1>}
      {description && (
        <p className="fps-description" data-testid="fps-description">{description}</p>
      )}
    </div>
    {(actions || secondaryActions) && (
      <div className="fps-actions" data-testid="fps-actions">
        {secondaryActions && (
          <div className="fps-actions-secondary">{secondaryActions}</div>
        )}
        {actions && (
          <div className="fps-actions-primary">{actions}</div>
        )}
      </div>
    )}
  </div>
);

FeaturePageHeader.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  actions: PropTypes.node,
  secondaryActions: PropTypes.node,
  eyebrow: PropTypes.node,
};

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------
const FeaturePageShell = ({
  title,
  description,
  actions,
  secondaryActions,
  children,
  sidebar,
  width,
  isLoading,
  error,
  onRetry,
}) => {
  const widthClass = WIDTH_CLASS[width] || WIDTH_CLASS.default;

  const body = (
    <div className="fps-body" data-testid="fps-body">
      {isLoading && <FeaturePageLoading />}
      {!isLoading && error && <FeaturePageError error={error} onRetry={onRetry} />}
      {!isLoading && !error && children}
    </div>
  );

  return (
    <div className={`feature-page-shell ${widthClass}`} data-testid="feature-page-shell">
      <div className="fps-layout">
        {(title || description || actions || secondaryActions) && (
          <FeaturePageHeader title={title} description={description} actions={actions} secondaryActions={secondaryActions} />
        )}
        {sidebar ? (
          <div className="fps-layout--with-sidebar" data-testid="fps-content-layout">
            <div className="fps-layout-main">
              {body}
            </div>
            <aside className="fps-layout-sidebar">{sidebar}</aside>
          </div>
        ) : body}
      </div>
    </div>
  );
};

FeaturePageShell.propTypes = {
  /** Page heading shown in the header bar. */
  title: PropTypes.node,
  /** One-line contextual description shown below the title. */
  description: PropTypes.node,
  /** Primary page-level actions (save / apply / discard). */
  actions: PropTypes.node,
  /** Secondary page-level controls (help link, docs link). */
  secondaryActions: PropTypes.node,
  /** Page content — not rendered while isLoading or error is set. */
  children: PropTypes.node,
  /** Optional right-column sidebar content. */
  sidebar: PropTypes.node,
  /** Container width preset. */
  width: PropTypes.oneOf(['default', 'wide', 'expanded', 'fluid']),
  /** Show loading spinner; suppresses children and error. */
  isLoading: PropTypes.bool,
  /** Error message or object; shows degraded state. */
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Retry callback shown with the error state. */
  onRetry: PropTypes.func,
};

FeaturePageShell.defaultProps = {
  title: null,
  description: null,
  actions: null,
  secondaryActions: null,
  children: null,
  sidebar: null,
  width: 'default',
  isLoading: false,
  error: null,
  onRetry: null,
};

export default FeaturePageShell;
